import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, Filter, Building2, Home, MapPin } from 'lucide-react';
import { format, startOfWeek, addDays, isSameDay, startOfMonth, endOfMonth, eachWeekOfInterval, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import Button from '../ui/Button';

interface Client {
  id: string;
  name: string;
  company_name?: string;
}

interface Squad {
  id: string;
  name: string;
  color: string | null;
}

interface Profile {
  user_id: string;
  sales_name: string | null;
  squad_id: string | null;
}

interface ScheduleDay {
  id: string;
  date: string;
  day_type: string;
  client_id: string | null;
  sdr_id: string | null;
  squad_id: string | null;
  slot?: string;
  work_location?: string | null;
  client?: Client;
  sdr?: Profile;
  squad?: Squad;
}

interface WeeklyCalendarViewProps {
  schedules: ScheduleDay[];
  selectedMonth: Date;
  clients: Client[];
  sdrs: Profile[];
  squads: Squad[];
  onSlotClick?: (date: Date, slot?: string, schedule?: ScheduleDay) => void;
  onWorkLocationChange?: (scheduleId: string, newLocation: string | null) => void;
  recentlyUpdatedIds?: Set<string>;
  defaultSdrFilter?: string;
}

const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

const DAY_TYPE_COLORS: Record<string, string> = {
  prospection: 'bg-green-500',
  congé: 'bg-yellow-500',
  conge: 'bg-yellow-500',
  férié: 'bg-red-600',
  ferie: 'bg-red-600',
  formation: 'bg-cyan-500',
  malade: 'bg-red-500',
  maladie: 'bg-red-500',
  'Jour Off': 'bg-blue-500',
  autre: 'bg-orange-500',
};

const DAY_TYPE_LABELS: Record<string, string> = {
  prospection: 'Prospection',
  malade: 'Maladie',
  congé: 'Congé',
  férié: 'Férié',
  formation: 'Formation',
  'Jour Off': 'Jour Off',
  autre: 'Autre'
};

const WeeklyCalendarView: React.FC<WeeklyCalendarViewProps> = ({
  schedules,
  selectedMonth,
  clients,
  sdrs,
  squads,
  onSlotClick,
  onWorkLocationChange,
  recentlyUpdatedIds = new Set(),
  defaultSdrFilter = 'all',
}) => {
  const [filterSdr, setFilterSdr] = useState<string>(defaultSdrFilter);
  const [filterClient, setFilterClient] = useState<string>('all');
  const [filterSquad, setFilterSquad] = useState<string>('all');
  const [filterDayType, setFilterDayType] = useState<string>('all');

  const weeks = useMemo(() => {
    const start = startOfMonth(selectedMonth);
    const end = endOfMonth(selectedMonth);
    const allWeeks = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });

    return allWeeks.map((weekStart, index) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      return {
        index: index + 1,
        label: `S${index + 1}`,
        start: weekStart,
        end: weekEnd,
        days: Array.from({ length: 5 }, (_, i) => addDays(weekStart, i))
      };
    });
  }, [selectedMonth]);

  const filteredSchedules = useMemo(() => {
    return schedules.filter(schedule => {
      const vacationTypes = ['congé', 'conge', 'férié', 'ferie'];
      const isVacation = vacationTypes.includes(schedule.day_type.toLowerCase());

      if (filterSdr !== 'all' && schedule.sdr_id !== filterSdr) {
        if (defaultSdrFilter !== 'all' && isVacation) {
          return false;
        }
        return false;
      }

      if (filterClient !== 'all' && schedule.client_id !== filterClient) return false;
      if (filterSquad !== 'all' && schedule.squad_id !== filterSquad) return false;
      if (filterDayType !== 'all' && schedule.day_type !== filterDayType) return false;
      return true;
    });
  }, [schedules, filterSdr, filterClient, filterSquad, filterDayType, defaultSdrFilter]);

  const getSchedulesForDay = (date: Date) => {
    return filteredSchedules.filter(s => isSameDay(new Date(s.date), date));
  };

  const cycleWorkLocation = (scheduleId: string, currentLocation: string | null | undefined) => {
    if (!onWorkLocationChange) return;
    const cycle: Array<string | null> = [null, 'office', 'remote', 'other'];
    const currentIdx = cycle.indexOf(currentLocation || null);
    const nextValue = cycle[(currentIdx + 1) % cycle.length];
    onWorkLocationChange(scheduleId, nextValue);
  };

  const getWorkLocationLabel = (location: string | null | undefined) => {
    if (location === 'office') return 'Bureau';
    if (location === 'remote') return 'Télétravail';
    if (location === 'other') return 'Autre';
    return 'Non défini';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center space-x-2 mb-2">
          <Filter className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">SDR</label>
            <select
              value={filterSdr}
              onChange={(e) => setFilterSdr(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les SDRs</option>
              {sdrs.map((sdr) => (
                <option key={sdr.user_id} value={sdr.user_id}>
                  {sdr.sales_name || sdr.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Squad</label>
            <select
              value={filterSquad}
              onChange={(e) => setFilterSquad(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes les squads</option>
              {squads.map((squad) => (
                <option key={squad.id} value={squad.id}>
                  {squad.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Type de journée</label>
            <select
              value={filterDayType}
              onChange={(e) => setFilterDayType(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les types</option>
              <option value="prospection">Prospection</option>
              <option value="conge">Congé</option>
              <option value="ferie">Férié</option>
              <option value="formation">Formation</option>
              <option value="maladie">Maladie</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              Vue Calendrier - {format(selectedMonth, 'MMMM yyyy', { locale: fr })}
            </h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="p-3 text-left text-sm font-semibold text-gray-700 w-24">
                  Semaine
                </th>
                {WEEKDAYS.map((day, idx) => (
                  <th key={idx} className="p-3 text-center text-sm font-semibold text-gray-900 border-l border-gray-200">
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week.label} className="border-b border-gray-200">
                  <td className="p-3 bg-gray-50 border-r border-gray-200">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{week.label}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {format(week.start, 'd MMM', { locale: fr })}
                      </div>
                      <div className="text-xs text-gray-500">au</div>
                      <div className="text-xs text-gray-600">
                        {format(week.end, 'd MMM', { locale: fr })}
                      </div>
                    </div>
                  </td>
                  {week.days.map((day, dayIdx) => {
                    const daySchedules = getSchedulesForDay(day);
                    const isCurrentMonth = day.getMonth() === selectedMonth.getMonth();

                    return (
                      <td
                        key={dayIdx}
                        className={`p-2 border-l border-gray-200 min-h-[150px] hover:bg-gray-50 transition-colors group relative ${
                          !isCurrentMonth ? 'bg-gray-100/50' : 'bg-white'
                        }`}
                      >
                        <div className="text-center mb-2">
                          <span className={`text-sm font-semibold ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                            {format(day, 'd')}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {daySchedules.length === 0 ? (
                            onSlotClick && (
                              <div
                                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                onClick={() => isCurrentMonth && onSlotClick(day, undefined, undefined)}
                              >
                                {isCurrentMonth && <Plus className="h-8 w-8 text-gray-300" />}
                              </div>
                            )
                          ) : (() => {
                            const ferieSchedules = daySchedules.filter(s => s.day_type === 'férié' || s.day_type === 'ferie');
                            const formationSchedules = daySchedules.filter(s => s.day_type === 'formation');
                            const otherSchedules = daySchedules.filter(s => s.day_type !== 'férié' && s.day_type !== 'ferie' && s.day_type !== 'formation');

                            return (
                              <>
                                {ferieSchedules.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={
                                      recentlyUpdatedIds.has(ferieSchedules[0].id)
                                        ? { opacity: 1, scale: [1, 1.05, 1], boxShadow: ['0 0 0px rgba(59, 130, 246, 0)', '0 0 15px rgba(59, 130, 246, 0.6)', '0 0 0px rgba(59, 130, 246, 0)'] }
                                        : { opacity: 1, scale: 1 }
                                    }
                                    transition={{ duration: 0.6 }}
                                    className={`bg-red-600 rounded px-1.5 py-1 ${onSlotClick ? 'cursor-pointer hover:shadow-lg' : ''} transition-all`}
                                    onClick={() => onSlotClick && onSlotClick(day, undefined, ferieSchedules[0])}
                                  >
                                    <div className="text-xs text-white text-center font-semibold">
                                      FÉRIÉ
                                    </div>
                                  </motion.div>
                                )}

                                {formationSchedules.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={
                                      recentlyUpdatedIds.has(formationSchedules[0].id)
                                        ? { opacity: 1, scale: [1, 1.05, 1], boxShadow: ['0 0 0px rgba(59, 130, 246, 0)', '0 0 15px rgba(59, 130, 246, 0.6)', '0 0 0px rgba(59, 130, 246, 0)'] }
                                        : { opacity: 1, scale: 1 }
                                    }
                                    transition={{ duration: 0.6 }}
                                    className={`bg-cyan-500 rounded px-1.5 py-1 ${onSlotClick ? 'cursor-pointer hover:shadow-lg' : ''} transition-all`}
                                    onClick={() => onSlotClick && onSlotClick(day, undefined, formationSchedules[0])}
                                  >
                                    <div className="text-xs text-white text-center font-semibold">
                                      FORMATION
                                    </div>
                                  </motion.div>
                                )}

                                {otherSchedules.map((schedule) => {
                                  const squad = schedule.squad_id
                                    ? squads.find((sq) => sq.id === schedule.squad_id)
                                    : null;

                                  let displayText = '';
                                  if (schedule.day_type === 'prospection') {
                                    displayText = `${schedule.sdr?.sales_name || 'SDR'} - ${schedule.client?.name || 'Client'}`;
                                  } else if (schedule.day_type === 'congé' || schedule.day_type === 'conge') {
                                    displayText = `${schedule.sdr?.sales_name || 'SDR'} - Congé`;
                                  } else if (schedule.day_type === 'malade' || schedule.day_type === 'maladie') {
                                    displayText = `${schedule.sdr?.sales_name || 'SDR'} - Maladie`;
                                  } else if (schedule.day_type === 'Jour Off') {
                                    displayText = `${schedule.sdr?.sales_name || 'SDR'} - Jour Off`;
                                  } else {
                                    const dayTypeLabel = DAY_TYPE_LABELS[schedule.day_type] || schedule.day_type;
                                    displayText = `${schedule.sdr?.sales_name || 'SDR'} - ${dayTypeLabel}`;
                                  }

                                  const isRecentlyUpdated = recentlyUpdatedIds.has(schedule.id);

                                  const getCellBg = () => {
                                    if (schedule.day_type === 'prospection') {
                                      if (schedule.work_location === 'remote') return 'bg-violet-500';
                                      if (schedule.work_location === 'other') return 'bg-teal-500';
                                      return 'bg-green-500';
                                    }
                                    return DAY_TYPE_COLORS[schedule.day_type] || 'bg-gray-600';
                                  };

                                  return (
                                    <motion.div
                                      key={schedule.id}
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={
                                        isRecentlyUpdated
                                          ? { opacity: 1, scale: [1, 1.05, 1], boxShadow: ['0 0 0px rgba(59, 130, 246, 0)', '0 0 15px rgba(59, 130, 246, 0.6)', '0 0 0px rgba(59, 130, 246, 0)'] }
                                          : { opacity: 1, scale: 1 }
                                      }
                                      transition={{ duration: 0.6 }}
                                      className={`${getCellBg()} rounded px-1.5 py-1 ${onSlotClick ? 'cursor-pointer hover:shadow-lg' : ''} transition-all relative`}
                                      style={{
                                        borderLeft: squad?.color && schedule.day_type === 'prospection' ? `3px solid ${squad.color}` : undefined,
                                      }}
                                      onClick={() => onSlotClick && onSlotClick(day, undefined, schedule)}
                                    >
                                      <div className="text-xs text-white truncate pr-5">
                                        {displayText}
                                      </div>
                                      {onWorkLocationChange ? (
                                        <button
                                          className="absolute top-0.5 right-0.5 p-0.5 rounded-md transition-colors shadow-sm"
                                          style={{
                                            backgroundColor: schedule.work_location === 'office' ? '#f59e0b'
                                              : schedule.work_location === 'remote' ? '#3b82f6'
                                              : schedule.work_location === 'other' ? '#6b7280'
                                              : 'transparent'
                                          }}
                                          title={`Lieu : ${getWorkLocationLabel(schedule.work_location)} (cliquer pour changer)`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            cycleWorkLocation(schedule.id, schedule.work_location);
                                          }}
                                        >
                                          {schedule.work_location === 'office' && <Building2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                                          {schedule.work_location === 'remote' && <Home className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                                          {schedule.work_location === 'other' && <MapPin className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                                          {!schedule.work_location && (
                                            <div className="w-3.5 h-3.5 rounded border-2 border-dashed border-white/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                          )}
                                        </button>
                                      ) : schedule.work_location ? (
                                        <div
                                          className="absolute top-0.5 right-0.5 p-0.5 rounded-md shadow-sm"
                                          style={{
                                            backgroundColor: schedule.work_location === 'office' ? '#f59e0b'
                                              : schedule.work_location === 'remote' ? '#3b82f6'
                                              : '#6b7280'
                                          }}
                                          title={getWorkLocationLabel(schedule.work_location)}
                                        >
                                          {schedule.work_location === 'office' && <Building2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                                          {schedule.work_location === 'remote' && <Home className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                                          {schedule.work_location === 'other' && <MapPin className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                                        </div>
                                      ) : null}
                                    </motion.div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>

                        {daySchedules.length > 0 && isCurrentMonth && onSlotClick && (
                          <div
                            className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => onSlotClick(day, undefined, undefined)}
                          >
                            <div className="bg-blue-600 hover:bg-blue-700 rounded-full p-1 cursor-pointer">
                              <Plus className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {filteredSchedules.length} journée(s) affichée(s) sur {schedules.length} au total
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded" />
              <span className="text-xs text-gray-700">Bureau</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-violet-500 rounded" />
              <span className="text-xs text-gray-700">Télétravail</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-teal-500 rounded" />
              <span className="text-xs text-gray-700">Autre lieu</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded" />
              <span className="text-xs text-gray-700">Congé</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-600 rounded" />
              <span className="text-xs text-gray-700">Férié</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-cyan-500 rounded" />
              <span className="text-xs text-gray-700">Formation</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded" />
              <span className="text-xs text-gray-700">Maladie</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyCalendarView;
